import { createContext, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query"; // Import TanStack Query
import { getRequest } from "services/api";
import Swal from "sweetalert2";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";

const StockHistoryContext = createContext();

export const StockHistoryProvider = ({ children }) => {
    const [filterOptions, setFilterOptions] = useState({
        search: "",
        currentPage: 1,
        filters: {}, // ✅ wajib ada ini
    });

    const handleFilterByMenu = (menuId) => {
        setFilterOptions((prev) => ({
            ...prev,
            filters: {
                ...prev.filters,
                "menu_id": menuId,
            },
            // currentPage: 1, // Reset halaman saat filter berubah
        }));
    };

    const queryString = buildQueryString({
        search: filterOptions.search || "",
        page: filterOptions.currentPage,
        paginate: 5,
        include: "menu,supplier,createdBy",
        ...(filterOptions.filters["menu_id"] && {
            [`filter[menu_id]`]: filterOptions.filters["menu_id"],
        }),
    });

    // Fetch data menggunakan TanStack Query
    const {
        data: stockHistories,
        error,
        isLoading,
        refetch, // Untuk manual refresh data
    } = useQuery({
        queryKey: ["stockHistories", filterOptions],
        queryFn: async () => {
            const response = await getRequest(`/stock-history${queryString}`);
            if (!response.success) {
                throw new Error(response.message || "Failed to fetch stock histories.");
            }
            return response;
        },
        keepPreviousData: true, // Agar tidak berkedip saat berpindah halaman
    });

    // Handle pencarian (Enter untuk submit)
    const handleSearch = (e) => {
        if (e.key === "Enter") {
            if (!e.target.value.trim()) {
                Swal.fire({
                    toast: true,
                    position: "top-end",
                    icon: "warning",
                    title: "Search input cannot be empty!",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
                return;
            }
            setFilterOptions((prev) => ({
                ...prev,
                search: e.target.value,
                currentPage: 1, // Reset ke halaman pertama setelah pencarian
            }));
        }
    };

    const handlePageChange = (page) => {
        setFilterOptions((prev) => ({
            ...prev,
            currentPage: page,
        }));
    };

    const totalPages = stockHistories?.pagination?.last_page || 1;

    const Pagination = usePaginationWithComponent({
        totalPages,
        currentPage: filterOptions.currentPage,
        onPageChange: handlePageChange,
        visiblePages: 5,
    });

    return (
        <StockHistoryContext.Provider
            value={{
                stockHistories: stockHistories?.data || [],
                isLoading,
                error,
                filterOptions,
                handleSearch,
                handleFilterByMenu, // ⬅️ Tambahkan di sini
                refetchStockHistory: refetch, // Bisa dipanggil untuk refresh data
                Pagination,
            }}
        >
            {children}
        </StockHistoryContext.Provider>
    );
};

export const useStockHistory = () => {
    const context = useContext(StockHistoryContext);
    if (!context) {
        throw new Error("useStockHistory must be used within StockHistoryProvider");
    }
    return context;
};
